import { clsx } from 'clsx';

export interface PriceRange {
  type: 'range';
  minValue: string;
  maxValue: string;
}

export interface PriceSale {
  type: 'sale';
  previousValue: string;
  currentValue: string;
}

export type Price = string | PriceRange | PriceSale;

interface Props {
  className?: string;
  colorScheme?: 'light' | 'dark';
  price: Price;
}

/**
 * This component supports various CSS variables for theming. Here's a comprehensive list, along
 * with their default values:
 *
 * ```css
 * :root {
 *   --price-light-text: hsl(var(--foreground));
 *   --price-light-sale-text: hsl(var(--foreground));
 *   --price-dark-text: hsl(var(--background));
 *   --price-dark-sale-text: hsl(var(--background));
 * }
 * ```
 */
export function PriceLabel({ className, colorScheme = 'light', price }: Props) {
  if (typeof price === 'string') {
    return (
      <span
        className={clsx(
          'block font-medium tabular-nums',
          {
            light: 'text-(--price-light-text,hsl(var(--foreground)))',
            dark: 'text-(--price-dark-text,hsl(var(--background)))',
          }[colorScheme],
          className,
        )}
      >
        {price}
      </span>
    );
  }

  switch (price.type) {
    case 'range':
      return (
        <span
          className={clsx(
            'block font-medium tabular-nums',
            {
              light: 'text-(--price-light-text,hsl(var(--foreground)))',
              dark: 'text-(--price-dark-text,hsl(var(--background)))',
            }[colorScheme],
            className,
          )}
        >
          {price.minValue}
          &nbsp;&ndash;&nbsp;
          {price.maxValue}
        </span>
      );

    case 'sale':
      return (
        <span className={clsx('block font-medium tabular-nums', className)}>
          <span
            className={clsx(
              'font-light line-through decoration-[0.5px] opacity-70',
              {
                light: 'text-(--price-light-text,hsl(var(--foreground)))',
                dark: 'text-(--price-dark-text,hsl(var(--background)))',
              }[colorScheme],
            )}
          >
            {price.previousValue}
          </span>{' '}
          <span
            className={clsx(
              {
                light: 'text-(--price-light-sale-text,hsl(var(--foreground)))',
                dark: 'text-(--price-dark-sale-text,hsl(var(--background)))',
              }[colorScheme],
            )}
          >
            {price.currentValue}
          </span>
        </span>
      );

    default:
      return null;
  }
}
